import { fetchOwnedTokenIds } from "./lib/alchemyNfts.js";
import { envHasNftGallery, getStakingEnv } from "./lib/env.js";
import { owlexImageUrl } from "./lib/owlexPortrait.js";
import { showToast } from "./lib/toast.js";
import { formatTxError } from "./lib/txErrors.js";

export { owlexImageUrl };

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Safe inside double-quoted HTML attributes. */
function escapeAttr(s) {
  return String(s).replace(/&/g, "&amp;").replace(/"/g, "&quot;");
}

/** @type {Set<string>} */
let selectedWalletTokenIds = new Set();

function sortTokenIds(ids) {
  return [...ids].sort((a, b) => {
    const na = BigInt(a);
    const nb = BigInt(b);
    if (na < nb) return -1;
    if (na > nb) return 1;
    return 0;
  });
}

/**
 * PNG with cache-busting query (fresh load each gallery build).
 * @param {string} tokenId
 * @param {number} bust
 */
// owlexImageUrl exported from ./lib/owlexPortrait.js

/** Empty state — matches “no Owlex” styling (logo + short copy). */
export function emptyConnectWalletHtml() {
  return `<div class="nft-grid__empty nft-grid__empty--brand nft-grid__empty--with-logo">
    <img src="/gallery/logo.png" alt="" width="72" height="72" class="nft-grid__empty-logo" decoding="async" />
    <p class="nft-grid__empty-copy">Connect wallet</p>
  </div>`;
}

export function getSelectedStakingTokenId() {
  return getSelectedWalletTokenId();
}

/** @returns {string[]} Selected token ids (numeric sort). */
export function getSelectedWalletTokenIds() {
  return sortTokenIds(selectedWalletTokenIds);
}

/** Wallet (unstaked) NFT selection — first selected, or null. */
export function getSelectedWalletTokenId() {
  const ids = getSelectedWalletTokenIds();
  return ids.length ? ids[0] : null;
}

export function clearStakingSelection() {
  selectedWalletTokenIds = new Set();
  syncSelectionDom();
}

let loadSeq = 0;

/** @type {ReturnType<typeof setInterval> | null} */
let walletCooldownTimer = null;

function stopWalletCooldownTicker() {
  if (walletCooldownTimer != null) {
    clearInterval(walletCooldownTimer);
    walletCooldownTimer = null;
  }
}

/**
 * @param {bigint | null | undefined} cooldownEndsAtSec
 * @returns {{ kind: "none" } | { kind: "countdown"; timeStr: string; cooldownEndsAtSec: bigint }}
 */
function stakeCooldownEtaParts(cooldownEndsAtSec) {
  if (cooldownEndsAtSec == null || cooldownEndsAtSec === 0n) {
    return { kind: "none" };
  }
  const now = BigInt(Math.floor(Date.now() / 1000));
  if (now >= cooldownEndsAtSec) return { kind: "none" };
  const left = Number(cooldownEndsAtSec - now);
  const h = Math.floor(left / 3600);
  const m = Math.floor((left % 3600) / 60);
  const s = left % 60;
  const timeStr =
    h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${s}s` : `${s}s`;
  return { kind: "countdown", timeStr, cooldownEndsAtSec };
}

/**
 * @param {{ kind: "none" } | { kind: "countdown"; timeStr: string; cooldownEndsAtSec: bigint }} parts
 */
function walletStakeCooldownHtml(parts) {
  if (parts.kind !== "countdown") return "";
  const timeSafe = escapeHtml(parts.timeStr);
  const ariaSafe = escapeAttr(`Stake in ${parts.timeStr}`);
  return `<div class="nft-card__wallet-status nft-card__wallet-status--cooldown" aria-label="${ariaSafe}">
      <span class="nft-card__roost-pill nft-card__roost-pill--countdown">
        <span class="nft-card__roost-pill__label">Stake in</span>
        <span class="nft-card__roost-pill__time">${timeSafe}</span>
      </span>
    </div>`;
}

/**
 * @param {HTMLElement} grid
 * @param {`0x${string}`} userAddress
 */
function startWalletCooldownTicker(grid, userAddress) {
  stopWalletCooldownTicker();
  const tick = () => {
    const times = grid.querySelectorAll(
      ".nft-card--wallet-cooldown .nft-card__roost-pill__time"
    );
    if (times.length === 0) {
      stopWalletCooldownTicker();
      return;
    }
    const sample = grid.querySelector(
      ".nft-card--wallet-cooldown[data-cooldown-end]"
    );
    const endAttr = sample?.getAttribute("data-cooldown-end");
    const end = endAttr ? BigInt(endAttr) : 0n;
    const now = BigInt(Math.floor(Date.now() / 1000));
    if (end === 0n || now >= end) {
      stopWalletCooldownTicker();
      window.dispatchEvent(
        new CustomEvent("owlex:staking-refresh", { detail: { address: userAddress } })
      );
      return;
    }
    const left = Number(end - now);
    const h = Math.floor(left / 3600);
    const m = Math.floor((left % 3600) / 60);
    const sec = left % 60;
    const timeStr =
      h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${sec}s` : `${sec}s`;
    times.forEach((el) => {
      el.textContent = timeStr;
    });
  };
  tick();
  walletCooldownTimer = setInterval(tick, 1000);
}

function syncSelectionDom() {
  document.querySelectorAll("#nft-grid .nft-card[data-token-id]").forEach((el) => {
    const id = el.getAttribute("data-token-id");
    const on = id != null && selectedWalletTokenIds.has(id);
    el.classList.toggle("nft-card--selected", on);
    el.setAttribute("aria-pressed", on ? "true" : "false");
  });

  window.dispatchEvent(
    new CustomEvent("owlex:staking-selection", {
      detail: { tokenIds: getSelectedWalletTokenIds() },
    })
  );
}

/**
 * @param {HTMLElement} grid
 */
function bindStakingSelection(grid) {
  grid.addEventListener("click", (e) => {
    const card = e.target.closest(".nft-card[data-token-id]");
    if (!card) return;
    const id = card.getAttribute("data-token-id");
    if (id == null) return;
    if (selectedWalletTokenIds.has(id)) selectedWalletTokenIds.delete(id);
    else selectedWalletTokenIds.add(id);
    syncSelectionDom();
  });

  grid.addEventListener("keydown", (e) => {
    const card = e.target.closest(".nft-card[data-token-id]");
    if (!card) return;
    if (e.key !== "Enter" && e.key !== " ") return;
    e.preventDefault();
    const id = card.getAttribute("data-token-id");
    if (id == null) return;
    if (selectedWalletTokenIds.has(id)) selectedWalletTokenIds.delete(id);
    else selectedWalletTokenIds.add(id);
    syncSelectionDom();
  });
}

/**
 * @typedef {{ excludeTokenIds?: string[] }} NftGallerySyncOptions
 */

/**
 * @param {string | null | undefined} address
 * @param {NftGallerySyncOptions} [options]
 */
export async function syncNftGallery(address, options) {
  const grid = document.getElementById("nft-grid");
  const countEl = document.getElementById("stat-owned");
  const env = getStakingEnv();
  const nftContract = env.owlexNft;
  const alchemyKey = env.alchemyApiKey;

  if (!grid) return;

  stopWalletCooldownTicker();

  const mySeq = ++loadSeq;
  selectedWalletTokenIds = new Set();
  syncSelectionDom();

  if (!address) {
    if (countEl) countEl.textContent = "—";
    grid.innerHTML = emptyConnectWalletHtml();
    return;
  }

  if (!envHasNftGallery(env)) {
    if (countEl) countEl.textContent = "—";
    grid.innerHTML = `<p class="nft-grid__empty nft-grid__empty--brand">Couldn’t load your Owlex.</p>`;
    return;
  }

  grid.innerHTML = `<p class="nft-grid__loading" role="status">Loading…</p>`;
  if (countEl) countEl.textContent = "…";

  try {
    let tokenIds = await fetchOwnedTokenIds(address, nftContract, alchemyKey);
    const exclude = new Set(options?.excludeTokenIds ?? []);
    if (exclude.size > 0) {
      tokenIds = tokenIds.filter((id) => !exclude.has(id));
    }
    if (mySeq !== loadSeq) return;

    if (countEl) countEl.textContent = String(tokenIds.length);

    if (tokenIds.length === 0) {
      grid.innerHTML = `<div class="nft-grid__empty nft-grid__empty--brand nft-grid__empty--with-logo">
        <img src="/gallery/logo.png" alt="" width="72" height="72" class="nft-grid__empty-logo" decoding="async" />
        <p class="nft-grid__empty-copy">${exclude.size > 0 ? "All Owlex are active." : "No Owlex found."}</p>
      </div>`;
      return;
    }

    const bust = Date.now();

    const cards = tokenIds
      .map(
        (id) => `
      <article
        class="nft-card nft-card--selectable"
        data-token-id="${escapeAttr(id)}"
        role="button"
        tabindex="0"
        aria-pressed="false"
      >
        <div class="nft-card__frame">
          <img
            class="nft-card__img"
            src="${owlexImageUrl(id, bust)}"
            alt="Owlex #${escapeHtml(id)}"
            width="280"
            height="280"
            loading="lazy"
            decoding="async"
          />
        </div>
        <p class="nft-card__id">#${escapeHtml(id)}</p>
      </article>`
      )
      .join("");

    grid.innerHTML = `<div class="nft-grid__inner" id="nft-grid-inner">${cards}</div>`;
    bindStakingSelection(grid);
    syncSelectionDom();
  } catch (err) {
    if (mySeq !== loadSeq) return;
    console.error("[owlex-staking] NFT load failed", err);
    if (countEl) countEl.textContent = "—";
    showToast(formatTxError(err), { variant: "error" });
    grid.innerHTML = `<p class="nft-grid__empty nft-grid__empty--brand">Couldn’t load.</p>`;
  }
}
